import { useMemo, useState } from 'react';
import Head from 'next/head';
import { DropdownItem, Image } from '@vgn-medien-holding/vgn-fe-components';
import { Heading } from '@vgn-medien-holding/vgn-react-components';
import { nanoid } from 'nanoid';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { twMerge } from 'tailwind-merge';
import { Button } from '@components/atoms/Button/Button';
import { Dropdown } from '@components/atoms/Dropdown/Dropdown';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { AboBar } from '@components/molecules/AboBar/AboBar';
import { AboBoxes } from '@components/molecules/AboBoxes/AboBoxes';
import { AboSticker } from '@components/molecules/AboSticker/AboSticker';
import { ReviewCard } from '@components/molecules/ReviewCard/ReviewCard';
import { DefaultOnlyLogo } from '@components/templates/layouts/DefaultOnlyLogo';
import { GetBySlugFullDocument, SubscriptionPage } from '@src/lib/graphql/generated';
import { decodeOptions } from '@src/utils/urlHelpers';
import { createUrqlClient } from '@lib/graphql/urql';

export function AboPage({ data }: { data: SubscriptionPage & { subscriptionBoxes: SubscriptionPage['boxes'] } }) {
  const [selectedOption, setSelectedOption] = useState<Partial<DropdownItem>>(null);
  const {
    subscriptionBoxes,
    feedback,
    feedback_headline,
    hide_banner,
    button_headline,
    buttons,
    additional_text,
    banner_text,
    metadata,
    top_disclaimer,
    top_headline,
    top_subheadline,
    boxes_more,
    top_image,
    breaker_text,
    cta,
    cta_link,
    use_multiple_options,
    multiple_options,
    price,
    price_extra,
  } = data;
  const ctaText = cta?.split('{{price}}') ?? [];
  const ctaLabel = ctaText?.length > 1 ? `${ctaText[0]} ${price} ${price_extra ?? ''} ${ctaText[1]}` : ctaText[0];

  const multipleOptions = useMemo(() => multiple_options.map((e) => ({ ...e, id: nanoid() })), [multiple_options]);
  const selectedOptionItem = multipleOptions.find((e) => e.id === selectedOption?.id);

  return (
    <>
      <Head>{Meta(metadata)}</Head>
      <div className="absolute inset-x-0 top-0 h-screen w-full opacity-75">
        <Image copyright="" src="/assets/tvm_assets/bg-waves.png" sizes="100vw" alt="" fill imageMode="cover" />
        <div className="absolute bottom-0 left-0 h-1/2 w-full bg-gradient-to-t from-black" />
      </div>
      <div className="relative">
        {!hide_banner && <AboBar bannerText={banner_text} price={price} priceExtra={price_extra} link={cta_link} />}
        <div className="mb-32 grid min-h-[50vh] grid-cols-3 items-center portrait-giant:min-h-[640px]">
          <div className="col-span-3 lg:col-span-2">
            <div className="mb-6">
              <Heading classProps={{ heading: 'text-heading-1 font-herokid' }} level={1}>
                {top_headline}
              </Heading>
              {top_subheadline && (
                <div className="prose prose-streaming mt-2 text-white">
                  {selectedOptionItem ? selectedOptionItem?.option_subheadline : top_subheadline}
                </div>
              )}
            </div>
            <div className="relative h-76 w-full pb-4 lg:hidden">
              <AboSticker text={breaker_text} price={price_extra ? `${price} ${price_extra}` : price} />
              <Image
                copyright={
                  selectedOptionItem ? selectedOptionItem.option_image?.copyright_text : top_image?.copyright_text
                }
                src={selectedOptionItem ? selectedOptionItem.option_image?.url : top_image?.url}
                alt={selectedOptionItem ? selectedOptionItem.option_image?.alt : top_image?.alt}
                fill
                imageMode="contain"
                sizes="(min-width: 1280) 33vw, 100vw"
                priority
              />
            </div>
            <div
              className="checkmark-list prose prose-streaming max-w-2xl text-white prose-a:text-white prose-li:my-1"
              dangerouslySetInnerHTML={{
                __html: selectedOptionItem ? selectedOptionItem.option_benefits : additional_text,
              }}
            ></div>
            {use_multiple_options && (
              <Dropdown
                items={multipleOptions?.map((e) => ({ id: e.id + '', label: e.option_title })) ?? []}
                classProps={{ root: 'max-w-screen-xs w-full pt-6 pb-3' }}
                selectedItem={selectedOption}
                setSelectedItem={(item) => {
                  setSelectedOption(item);
                }}
                placeholder="Gutschein auswählen"
              />
            )}
            {!use_multiple_options && buttons?.length > 0 && (
              <div>
                <div className="heading-2 mb-8 text-center" dangerouslySetInnerHTML={{ __html: button_headline }}></div>
                <div
                  className={twMerge(
                    '',
                    buttons.length > 1 &&
                      'mx-auto grid max-w-sm flex-wrap gap-4 sm:flex sm:max-w-lg sm:justify-between',
                  )}
                >
                  {buttons?.map((button, index) => (
                    <Button
                      key={index}
                      link={button?.button_link}
                      title={button?.button_text}
                      classProps={{
                        root: 'justify-center pointer-events-auto my-0 !bg-primary hover:!bg-transparent',
                      }}
                      buttonStyle="primary"
                    />
                  ))}
                </div>
              </div>
            )}
            {use_multiple_options && (
              <Button
                key={selectedOptionItem?.id ?? 'nothing-chosen'}
                link={selectedOptionItem?.option_buy_link}
                onAction={null}
                title={'Jetzt bestellen'}
                disabled={!selectedOption?.id}
                hasAction={!selectedOption?.id}
                classProps={{
                  root: 'justify-center pointer-events-auto my-0',
                }}
                buttonStyle="primary"
              />
            )}
            <div
              className="prose mx-auto mt-8 text-xs text-white prose-a:text-white"
              dangerouslySetInnerHTML={{
                __html: selectedOptionItem ? selectedOptionItem.option_disclaimer : top_disclaimer,
              }}
            ></div>
          </div>
          <div className="relative ml-8 hidden size-full lg:block">
            <AboSticker text={breaker_text} price={price_extra ? `${price} ${price_extra}` : price} />
            <Image
              sizes="40vw"
              copyright={top_image?.copyright_text}
              src={top_image?.url}
              alt={top_image?.alt}
              fill
              imageMode="contain"
            />
          </div>
        </div>

        <AboBoxes
          classProps={{ root: 'max-w-[60rem] mx-auto prose-a:underline prose-a:text-white' }}
          boxes={subscriptionBoxes}
        />

        <div className="mb-20 flex flex-col items-center">
          {boxes_more && (
            <Heading level={4} classProps={{ heading: 'text-heading-4 mt-16 text-center font-herokid' }}>
              {boxes_more}
            </Heading>
          )}
          <Button
            title={ctaLabel}
            link={cta_link}
            hasAction={false}
            classProps={{ root: 'mt-8 mb-2 inline-block pointer-events-auto' }}
            buttonStyle="primary"
          />
        </div>

        <div className="mb-20 mt-16 flex flex-col items-center">
          <Heading level={2} classProps={{ heading: 'text-heading-2 mb-8 font-herokid text-center' }}>
            {feedback_headline}
          </Heading>
          <div className="grid gap-6 lg:grid-cols-3">
            {feedback?.map((review, index) => <ReviewCard key={index} {...review} />)}
          </div>
          <Button
            title={ctaLabel}
            link={cta_link}
            hasAction={false}
            classProps={{ root: 'inline-block pointer-events-auto mt-8 mb-2' }}
            buttonStyle="primary"
          />
        </div>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { ssrQuery } = createUrqlClient();

  const options = decodeOptions(params?.slug);

  const { data } = await ssrQuery({
    query: GetBySlugFullDocument,
    variables: {
      portal: 'tvmedia',
      slug: `abo/${options.slug}`,
    },
  });

  if (!data?.findBySlug) {
    return { notFound: true };
  }

  return {
    revalidate: 60 * 60 * 24 * 2, // 2 days
    props: { data: data?.findBySlug ?? null },
  };
};

AboPage.PageLayout = DefaultOnlyLogo;

export default AboPage;
